import React, { useRef, useState } from 'react';
import {
  DECISION_SPEED_OPTIONS,
  DEFAULT_AUDIENCE_INPUTS,
  HOW_STEPS,
  NICHE_OPTIONS,
  SALES_FORMAT_OPTIONS,
  generateJTBDProfile,
  validateAudienceInputs,
} from './audienceBuilderLogic';
import './AudienceBuilder.css';

const ListBlock = ({ title, items }) => (
  <div className="ab-block">
    <div className="ab-block-title">{title}</div>
    <ul className="ab-list">{items.map((item, i) => <li key={i}>{item}</li>)}</ul>
  </div>
);

const AudienceBuilder = () => {
  const [inputs, setInputs] = useState(DEFAULT_AUDIENCE_INPUTS);
  const [errors, setErrors] = useState({});
  const [profile, setProfile] = useState(null);
  const resultsRef = useRef(null);

  const setField = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setInputs((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleGenerate = () => {
    const nextErrors = validateAudienceInputs(inputs);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setProfile(null);
      return;
    }
    const result = generateJTBDProfile(inputs);
    setProfile(result);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleReset = () => {
    setInputs(DEFAULT_AUDIENCE_INPUTS);
    setErrors({});
    setProfile(null);
  };

  const handlePrint = () => window.print();

  return (
    <div className="ab-tool">
      <div className="ab-disclaimer">{'Данные носят ознакомительный характер и не являются готовым маркетинговым исследованием. Используйте как отправную точку для глубинных интервью с клиентами.'}</div>

      <div className="ab-form-section">
        <div className="ab-section-title">{'Шаг 1 — о бизнесе'}</div>
        <div className="ab-form-grid">
          <label className="ab-field">
            <span className="ab-label">{'Название бизнеса (необязательно)'}</span>
            <input className="ab-input" value={inputs.businessName} onChange={setField('businessName')} />
          </label>

          <label className="ab-field">
            <span className="ab-label">{'Тип ниши'}</span>
            <select className="ab-input" value={inputs.niche} onChange={setField('niche')}>
              {NICHE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </label>

          <label className="ab-field ab-field--full">
            <span className="ab-label">{'Что продаёте / какую услугу оказываете'} *</span>
            <textarea className="ab-textarea" rows={3} value={inputs.product} onChange={setField('product')} />
            {errors.product ? <span className="ab-error">{errors.product}</span> : null}
          </label>

          <label className="ab-field">
            <span className="ab-label">{'Средний чек (₸)'}</span>
            <input className="ab-input" type="number" min={0} value={inputs.avgPrice} onChange={setField('avgPrice')} />
          </label>

          <label className="ab-field">
            <span className="ab-label">{'Город / регион'}</span>
            <input className="ab-input" value={inputs.city} onChange={setField('city')} />
          </label>

          <label className="ab-field ab-field--full">
            <span className="ab-label">{'Кто сейчас покупает'} *</span>
            <textarea className="ab-textarea" rows={2} value={inputs.whoBuys} onChange={setField('whoBuys')} />
            {errors.whoBuys ? <span className="ab-error">{errors.whoBuys}</span> : null}
          </label>

          <label className="ab-field ab-field--full">
            <span className="ab-label">{'Какую задачу решаете для клиента'} *</span>
            <textarea className="ab-textarea" rows={2} value={inputs.problemSolved} onChange={setField('problemSolved')} />
            {errors.problemSolved ? <span className="ab-error">{errors.problemSolved}</span> : null}
          </label>

          <label className="ab-field ab-field--full">
            <span className="ab-label">{'Конкуренты (необязательно)'}</span>
            <textarea className="ab-textarea" rows={2} value={inputs.competitors} onChange={setField('competitors')} />
          </label>

          <label className="ab-field">
            <span className="ab-label">{'Формат продаж'}</span>
            <select className="ab-input" value={inputs.salesFormat} onChange={setField('salesFormat')}>
              {SALES_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </label>

          <label className="ab-field">
            <span className="ab-label">{'Скорость принятия решения'}</span>
            <select className="ab-input" value={inputs.decisionSpeed} onChange={setField('decisionSpeed')}>
              {DECISION_SPEED_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="ab-actions">
          <button type="button" className="ab-btn ab-btn--primary" onClick={handleGenerate}>{'Собрать портрет ЦА'}</button>
          {profile ? (
            <>
              <button type="button" className="ab-btn" onClick={handlePrint}>{'Печать / PDF'}</button>
              <button type="button" className="ab-btn" onClick={handleReset}>{'Заполнить заново'}</button>
            </>
          ) : null}
        </div>
        <p className="ab-export-note">{'Экспорт в Excel — скоро'}</p>
      </div>

      {profile ? (
        <div ref={resultsRef} className="ab-results ab-print-area">
          <div className="ab-section-title">{'Результат'}</div>

          <div className="ab-section-title ab-sub">{'Работы (Jobs)'}</div>
          <ul className="ab-list ab-list--jobs">{profile.jobs.map((j, i) => <li key={i}>{j}</li>)}</ul>

          <div className="ab-section-title ab-sub">{'Сегментация'}</div>
          <div className="ab-segments">
            {profile.segments.map((seg) => (
              <div key={seg.id} className="ab-segment-card">
                <h3 className="ab-segment-title">{seg.title}</h3>
                <p className="ab-segment-desc">{seg.description}</p>
                <ListBlock title={'Работы (Jobs)'} items={seg.jobs} />
                <ListBlock title={'Боли'} items={seg.pains} />
                <ListBlock title={'Выгоды'} items={seg.gains} />
                <ListBlock title={'Возражения'} items={seg.objections} />
                <ListBlock title={'Отток: первичный'} items={seg.churnPrimary} />
                <ListBlock title={'Отток: вторичный'} items={seg.churnSecondary} />
              </div>
            ))}
          </div>

          <ListBlock title={'Каналы'} items={profile.channels} />
          <ListBlock title={'Факторы выбора'} items={profile.selectionFactors} />
          <ListBlock title={'Приоритеты для бизнеса'} items={profile.priorities} />
          <ListBlock title={'Выводы'} items={profile.conclusions} />

          <div className="ab-disclaimer ab-disclaimer--bottom">{'Данные носят ознакомительный характер и не являются готовым маркетинговым исследованием. Используйте как отправную точку для глубинных интервью с клиентами.'}</div>
        </div>
      ) : null}

      <div className="ab-how">
        <div className="ab-section-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="ab-how-step">
            <div className="ab-how-num">{step.num}</div>
            <div className="ab-how-text"><strong>{step.title}</strong> {step.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AudienceBuilder;
